import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiPlus, FiEdit, FiTrash2, FiUpload } from 'react-icons/fi';

const Admin = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    category: '',
    description: '',
    images: [],
    sizes: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [productsRes, ordersRes] = await Promise.all([
        axios.get('/api/products/admin'),
        axios.get('/api/orders/admin')
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newProduct.name);
      formData.append('price', newProduct.price);
      formData.append('category', newProduct.category);
      formData.append('description', newProduct.description);
      
      // Handle image upload
      newProduct.images.forEach((image, index) => {
        formData.append(`images[${index}]`, image);
      });

      await axios.post('/api/products/admin', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Product added successfully!');
      setIsAddingProduct(false);
      setNewProduct({ name: '', price: '', category: '', description: '', images: [], sizes: [] });
      fetchAdminData();
    } catch (error) {
      toast.error('Error adding product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`/api/products/admin/${id}`);
        toast.success('Product deleted successfully!');
        fetchAdminData();
      } catch (error) {
        toast.error('Error deleting product');
      }
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-5xl font-playfair font-bold bg-gradient-to-r from-gray-900 to-black bg-clip-text text-transparent">
            Admin Dashboard
          </h1>
          <p className="text-xl font-montserrat text-gray-600 mt-4">
            Manage products and orders for Ms Bridal Wear
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Add New Product */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 lg:p-12"
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-playfair font-bold text-gray-900 dark:text-white">
                Add New Product
              </h2>
              <motion.button
                onClick={() => setIsAddingProduct(!isAddingProduct)}
                className="p-3 bg-gradient-to-r from-gold to-yellow-500 rounded-2xl shadow-lg hover:shadow-gold/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiPlus className="w-6 h-6 text-black" />
              </motion.button>
            </div>

            {isAddingProduct && (
              <form onSubmit={handleAddProduct} className="space-y-6">
                <div>
                  <label className="block text-lg font-montserrat font-semibold mb-3">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:border-gold focus:outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-lg font-montserrat font-semibold mb-3">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:border-gold focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-montserrat font-semibold mb-3">
                      Category
                    </label>
                    <select
                      value={newProduct.category}
                      onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:border-gold focus:outline-none"
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="bridal-lehenga">Bridal Lehenga</option>
                      <option value="designer-sarees">Designer Sarees</option>
                      <option value="wedding-gowns">Wedding Gowns</option>
                      <option value="party-wear">Party Wear</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-montserrat font-semibold mb-3">
                    Description
                  </label>
                  <textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                    rows={4}
                    className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:border-gold focus:outline-none resize-vertical"
                    required
                  />
                </div>

                <div>
                  <label className="block text-lg font-montserrat font-semibold mb-3 flex items-center">
                    <FiUpload className="mr-2" />
                    Product Images
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => setNewProduct({...newProduct, images: Array.from(e.target.files)})}
                    className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:border-gold focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-black hover:file:bg-gold/90"
                    required
                  />
                  {newProduct.images.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {newProduct.images.length} image(s) selected
                    </p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-black font-bold py-4 px-8 rounded-2xl text-lg shadow-2xl hover:shadow-gold/50 font-montserrat transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                >
                  {loading ? 'Adding...' : 'Add Product'}
                </motion.button>
              </form>
            )}
          </motion.div>

          {/* Products List */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 lg:p-12 overflow-hidden"
          >
            <h2 className="text-3xl font-playfair font-bold mb-8 text-gray-900 dark:text-white">
              Products ({products.length})
            </h2>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {products.map((product) => (
                <motion.div
                  key={product._id}
                  className="flex items-center justify-between p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center space-x-4">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-20 h-20 object-cover rounded-xl shadow-lg"
                    />
                    <div>
                      <h4 className="font-semibold text-lg font-montserrat">{product.name}</h4>
                      <p className="text-gold font-bold">₹{product.price}</p>
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <motion.button
                      onClick={() => {/* Edit functionality */}}
                      className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-all"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Edit"
                    >
                      <FiEdit className="w-5 h-5" />
                    </motion.button>
                    
                    <motion.button
                      onClick={() => handleDeleteProduct(product._id)}
                      className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Delete"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl p-8 lg:p-12"
        >
          <h2 className="text-3xl font-playfair font-bold mb-8 text-gray-900 dark:text-white">
            Recent Orders ({orders.length})
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-4 font-montserrat font-semibold">Order ID</th>
                  <th className="text-left py-4 font-montserrat font-semibold">Customer</th>
                  <th className="text-left py-4 font-montserrat font-semibold">Amount</th>
                  <th className="text-left py-4 font-montserrat font-semibold">Status</th>
                  <th className="text-left py-4 font-montserrat font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 5).map((order) => (
                  <tr key={order._id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="py-4 font-montserrat">#ORD-{order._id.slice(-8)}</td>
                    <td className="py-4 font-montserrat">{order.customer.name}</td>
                    <td className="py-4 font-bold text-gold font-montserrat">
                      ₹{order.totalAmount.toLocaleString()}
                    </td>
                    <td className="py-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        order.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : order.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 font-montserrat">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;